import createAttachmentFromLocalFileService from '../services/createAttachmentFromLocalFile';
import { getErrorForAttachmentCreationFailure } from '../utils/getErrorForAttachmentCreationFailure';
import { shallowCopyAndReplaceValueNullWithUndefined } from '../utils/shallowCopyAndReplaceValueNullWithUndefined';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { assertNever } from 'owa-assert';
import { CompletionStatus, SliceAction, startBlobSlicingTask } from 'owa-blob-slicer';
import type { ClientItemId } from 'owa-client-ids';
import type AttachmentInfoResponseMessage from 'owa-service/lib/contract/AttachmentInfoResponseMessage';
import type AttachmentType from 'owa-service/lib/contract/AttachmentType';
import type CreateAttachmentResponse from 'owa-service/lib/contract/CreateAttachmentResponse';
import {
    AttachmentFullViewState,
    AttachmentState,
    AttachmentStateType,
    setActionCompletePercent,
} from 'owa-attachment-full-data';

export const SLICE_SIZE = 14 * 1024 * 1024; // 14 Mb

function isResponseMessageSuccess(response: AttachmentInfoResponseMessage) {
    return response && response.Attachments.length > 0 && response.Attachments[0];
}

/**
 * Creates an array of number upto the size and initializes
 * its values to 0.
 * @param size The size of the array to initialize
 */
function initializeEmptyNumberArray(size: number) {
    const arr = [];
    for (let i = 0; i < size; i++) {
        arr[i] = 0;
    }

    return arr;
}

/**
 * Creates a function that will upload the slice and also handles updating the progress
 * of the ongoing upload.
 */
function createActionToUploadSlice(
    parentItemId: ClientItemId,
    attachment: AttachmentType,
    cancellationId: string,
    backingObject: Blob,
    attachmentState: AttachmentState
): { action: SliceAction; getLastResponseMessage: () => AttachmentInfoResponseMessage } {
    let responseMessage: AttachmentInfoResponseMessage = null;
    let bytesUploadedForSlice: number[] | null = null; // Used to track the bytes uploaded for each slice

    const getLastResponseMessage = () => responseMessage;

    const action = async (blobSlice: Blob, sliceNumber: number, totalSlices: number) => {
        if (!bytesUploadedForSlice) {
            // If the array to track bytes uploaded is not initialized then initialize it
            // upto total slices with each value set to 0 by default
            bytesUploadedForSlice = initializeEmptyNumberArray(totalSlices);
        }

        let onProgressHandler = null;
        if (attachmentState.attachmentType === AttachmentStateType.Attachment) {
            onProgressHandler = function onAttachmentProgressHandler(e: ProgressEvent) {
                bytesUploadedForSlice[sliceNumber] = e.loaded;

                // To update progress sum all the bytes uploaded for all slices and divide by total
                // file size to the completion values is between 0-1
                const sumOfAllBytes = bytesUploadedForSlice.reduce((sum, val) => sum + val, 0);
                const loaded = sumOfAllBytes / backingObject.size;
                setActionCompletePercent(<AttachmentFullViewState>attachmentState, loaded);
            };
        }

        // filter out null properties and do not send them back to server
        const attachmentForServiceCall = shallowCopyAndReplaceValueNullWithUndefined(attachment);

        // Call the service to upload the slice
        const response: CreateAttachmentResponse = await createAttachmentFromLocalFileService(
            parentItemId,
            attachmentForServiceCall,
            cancellationId,
            blobSlice,
            onProgressHandler,
            { sliceNumber: sliceNumber, totalSlices: totalSlices }
        );

        // Update the last response message received from the server
        responseMessage = response.ResponseMessages.Items[0];

        if (isResponseMessageSuccess(responseMessage)) {
            // In case of the web resolver's first slice response, it contains the AttachmentId
            // of the newly created attachment. We need to pass this ID for the
            // subsequent slice requests hence we copy the attachment object and
            // update the attachment ID in it.
            // The reason to copy the attachment object is so that the passed object is not changed
            attachment = {
                ...attachment,
                AttachmentId: responseMessage.Attachments[0].AttachmentId,
            };

            // In case of the hx resolver, only the last slice will have attachment information,
        } else {
            // If its not a success message then something went wrong hence we throw
            throw getErrorForAttachmentCreationFailure(responseMessage);
        }
    };

    return {
        action: action,
        getLastResponseMessage: getLastResponseMessage,
    };
}

function logSlicedCreatePerfDatapoint(
    slicedPerfDatapoint: PerformanceDatapoint,
    size: number,
    succeeded: boolean,
    error?: Error
) {
    slicedPerfDatapoint.addCustomData([size]);

    if (succeeded) {
        slicedPerfDatapoint.end();
    } else {
        slicedPerfDatapoint.endWithError(DatapointStatus.ServerError, error);
    }
}

export const enum CreateAttachmentStatus {
    Completed,
    Cancelled,
}

export type AttachmentCreationResult =
    | { status: CreateAttachmentStatus.Completed; attachment: AttachmentType }
    | { status: CreateAttachmentStatus.Cancelled };

export interface CreateAttachmentFromLocalFileSlicedResult {
    onCompletePromise: Promise<AttachmentCreationResult>;
    cancel: () => void;
}

export interface CreateAttachmentFromLocalFileSlicedProperties {
    sliceSizeInBytes?: number;
}

export default function createAttachmentFromLocalFileSliced(
    parentItemId: ClientItemId,
    attachment: AttachmentType,
    cancellationId: string,
    backingObject: Blob,
    attachmentState: AttachmentState,
    properties: CreateAttachmentFromLocalFileSlicedProperties = {}
): CreateAttachmentFromLocalFileSlicedResult {
    properties.sliceSizeInBytes = properties.sliceSizeInBytes || SLICE_SIZE;

    const slicedPerfDatapoint = new PerformanceDatapoint(
        'AttachmentCreationClassicFromLocalFileSliced',
        {
            timeout: 300000, // five minutes
        }
    );

    const { action: sliceAction, getLastResponseMessage } = createActionToUploadSlice(
        parentItemId,
        attachment,
        cancellationId,
        backingObject,
        attachmentState
    );

    const { onCompletePromise, cancel } = startBlobSlicingTask(backingObject, sliceAction, {
        sliceSize: properties.sliceSizeInBytes,

        // As we currently do not support parallelization at backend
        // hence we keep it 1 slice at a time
        numberOfSlicesToParallelize: 1,
    });

    // Track if cancellation was invoked or not
    let isCancelled = false;
    const cancelUpload = () => {
        isCancelled = true;
        cancel();
    };

    const uploadPromise = onCompletePromise
        .then(
            (result: CompletionStatus): AttachmentCreationResult => {
                switch (result) {
                    case CompletionStatus.Completed:
                        logSlicedCreatePerfDatapoint(slicedPerfDatapoint, backingObject.size, true);
                        return {
                            status: CreateAttachmentStatus.Completed,
                            attachment: getLastResponseMessage().Attachments[0],
                        };
                    case CompletionStatus.Cancelled:
                        return {
                            status: CreateAttachmentStatus.Cancelled,
                        };
                    default:
                        return assertNever(result);
                }
            }
        )
        .catch(
            (error): AttachmentCreationResult => {
                if (isCancelled) {
                    // In case of cancelled we return the cancelled status as error
                    // is expected for any subsequent slice that was being uploaded
                    // while cancellation was initiated.
                    return {
                        status: CreateAttachmentStatus.Cancelled,
                    };
                }

                // Log the perf datapoint and re-throw the error so its handled at a higher level.
                // We log it only if its not cancelled as cancellation can result into error for
                // slices that were already being uploaded, hence logging them would give us
                // incorrect error rate.
                logSlicedCreatePerfDatapoint(slicedPerfDatapoint, backingObject.size, false, error);

                throw error;
            }
        );

    return {
        onCompletePromise: uploadPromise,
        cancel: cancelUpload,
    };
}
